/* @flow */
import Config from 'Config';

import apiRequest from 'lib/apiRequest';
import cookies from 'lib/Cookies';
import ChatActions from 'chat/actions/ChatActions';
import GoogleAPI from 'lib/GoogleAPI';
import PusherClient from 'pusher/PusherClient';
import Session from 'entities/Session';
import User from 'entities/User';
import ToastActions from 'actions/ToastActions';
import { dispatcher } from 'lib/Dispatcher';
import { getWindowUser } from 'lib/WindowUser';
import { promiseAppData } from 'promises/userBootData';
import { hardRedirectTo } from 'lib/ClientRouteLogic';
import { configureRollbar } from 'utils/setupErrorLogging';
import injectUserIntoApollo from 'auth/injectUserIntoApollo';
import { CSRF_COOKIE_KEY } from '../lib/requestHeaders';
import { ActionTypes } from 'auth/AuthConstants';

const SEC_IN_DAY = 60 * 60 * 24;

const AUTOLOGIN_AUTH_TOKEN = 'auth_token';
const REMEMBER_USER_TOKEN = 'remember_user_token';
const SESSION_UUID = 'session_uuid';
const PERSIST = 'persist';

function clearNonStoreAuthState(removeCSRF?: boolean = false) {
  // XXX until we get our Flux in order
  ChatActions.resetPromiseMemoization();
  cookies.expire(AUTOLOGIN_AUTH_TOKEN);
  cookies.expire(REMEMBER_USER_TOKEN);
  cookies.expire(SESSION_UUID);
  cookies.expire(PERSIST);

  if (removeCSRF) {
    cookies.expire(CSRF_COOKIE_KEY);
  }
  PusherClient.disconnectAndCleanUp();
}

function configureTrackJS(user: User): void {
  const { trackJs } = window;
  if (trackJs) {
    trackJs.configure({ userId: String(user.id) });
    // TJS should let us filter on application version, but it doesn't yet, so
    // they've advised using their generic filtering function
    trackJs.addMetadata('buildVersion', window._trackJs.version);
    // sent the whole thing as one value so we can filter by vectors of permissions
    trackJs.addMetadata('userPermissions', user.getPermissions());
    // like user.role but infers additional segments like "child"
    trackJs.addMetadata('userSegment', user.getTrackingSegment());
  }
}

function configureAppboy(user: User): void {
  const { appboy } = window;
  if (appboy && Config.Environment.APPBOY_ENVIRONMENT === 'production') {
    // this is ignored if appboy is already initialized.
    // we call this to make sure the client is initialized since
    // calling methods (like changeUser) will start throwing errors otherwise.
    // https://js.appboycdn.com/web-sdk/2.0/doc/module-appboy.html
    appboy.initialize(Config.Environment.APPBOY_PUBLIC_KEY, { enableHtmlInAppMessages: true });
    appboy.changeUser(user.id);
    appboy.openSession();
  }
}

const AuthActions = {
  onSessionCreated(user: User, fetchAppData?: boolean = true): Promise<User> {
    console.debug('onSessionCreated', user && user.id);

    const num: number = parseInt(user.id, 10);
    configureAppboy(user);
    configureTrackJS(user);
    configureRollbar({ id: num });

    injectUserIntoApollo(user);

    const dispatchUser = () => {
      dispatcher.dispatch({
        type: ActionTypes.AUTH_SUCCESS,
        user,
      });

      return user;
    };

    if (fetchAppData) {
      // Load user initial data before we dispatch AUTH_SUCCESS
      return promiseAppData(user).then(dispatchUser);
    } else {
      return Promise.resolve(dispatchUser());
    }
  },

  signIn(
    deviceAddress: string,
    password: string,
    emailConfirmationToken: ?string = null,
    persist: boolean = false
  ): Promise<?User> {
    const valid = deviceAddress && deviceAddress.length > 0 && password && password.length > 0;
    if (!valid) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ statusCode: 400 });
    }

    console.debug('signIn', { persist });

    function onError(error) {
      if (error) {
        console.warn('Session.create error', error, error.stack);
        if (error.statusCode !== 401) {
          console.error('Session.create error');
        }
      }

      dispatcher.dispatch({
        type: ActionTypes.AUTH_FAILURE,
        error,
      });

      return Promise.reject(error);
    }

    return Session.create(deviceAddress, password, emailConfirmationToken, persist)
      .then(this.onSessionCreated)
      .catch(onError);
  },

  createConfirmationCookie(deviceAddress: string) {
    return Session.create(deviceAddress, '').catch(err => err);
  },

  // TODO make Promise<void> or void
  accessTokenSignIn(token: string): Promise<User> {
    if (!(token.length > 0)) {
      console.error('accessTokenSignIn received bad token', token);
    }

    console.debug('accessTokenSignIn');

    return Session.createWithToken(token)
      .then(this.onSessionCreated)
      .catch(error => {
        dispatcher.dispatch({
          type: ActionTypes.AUTH_FAILURE,
          error,
        });

        return Promise.reject(error);
      });
  },

  expireSession(): void {
    AuthActions.signOut('/session/expired');
  },

  signoutWithoutRedirect(removeCSRF?: boolean = false): Promise<void> {
    const { appboy } = window;
    if (appboy && Config.Environment.APPBOY_ENVIRONMENT === 'production') {
      appboy.destroy();
    }
    return Promise.all([Session.destroy(), GoogleAPI.signOut()])
      .then(() => clearNonStoreAuthState(removeCSRF))
      .catch(error => {
        ToastActions.showErrorToast('There was a problem logging out');
        console.error('signOut failed');
        throw error;
      });
  },

  signOut(redirectTo?: string = '/send_the_app', removeCSRF?: boolean = false): Promise<void> {
    return this.signoutWithoutRedirect(removeCSRF).then(() => hardRedirectTo(redirectTo));
  },

  requestPasswordReset(uid: string) {
    return apiRequest('/password', 'POST', {
      user_identifier: uid,
    });
  },

  loadAppDataIfWindowUser(): Promise<void> {
    const user = getWindowUser();
    console.debug('loadAppDataIfWindowUser user: ', user && user.id);
    if (user) {
      // from exports so that this can be spied on in tests
      return this.onSessionCreated(user, true);
    } else {
      return Promise.resolve();
    }
  },

  checkLoggedInAndLoadAppData(): Promise<User> {
    return User.fetch().then(user => this.onSessionCreated(((user: any): User), true));
  },

  checkLoggedIn(): Promise<User> {
    return User.fetch().then(u => this.onSessionCreated(u, false));
  },

  jwtTokenSignIn(token: string) {
    dispatcher.dispatch({
      type: ActionTypes.TOKEN_ENTRY,
      token,
    });
  },

  googleSignIn(code: string, emailConfirmationToken: ?string = null) {
    return Session.createWithGoogleAuthorizationCode(code, emailConfirmationToken).then(
      this.onSessionCreated
    );
  },

  /**
   * Fetches and sets the token for autologin to native apps
   */
  setAutologinTokenCookie(): void {
    apiRequest('/access_tokens/confirmed_login', 'POST').then(
      response => {
        cookies.set(AUTOLOGIN_AUTH_TOKEN, response.token, {
          domain: Config.Environment.API_PROXY_COOKIE_DOMAIN,
          expires: 30 * SEC_IN_DAY,
        });
      },
      () => {}
    );
  },

  getAutologinTokenFromCookies(): ?string {
    if (!cookies.isEnabled()) {
      return null;
    }

    return cookies.get(AUTOLOGIN_AUTH_TOKEN);
  },
};

export default AuthActions;
